/**
 * The Fall catcher flask and its damage: skin, two crack overlays (same frame as the skin)
 * and two glass shards. Same logical frames as the PNGs they replace.
 *
 * The skin is letterboxed into the flask box exactly like flask.png was (full box height,
 * 256/383 of it wide, centred), and the colliders were tuned against where flask.png drew its
 * glass. So the silhouette below is traced from flask.png's alpha profile (outer ink edge):
 *   lip    y 8–44,  x 74–181      neck  y 44–116, x 83–172
 *   cone   x 78→7 (left) from y 132 to y 312, slope 0.394 px/px
 *   base   rounded corner y 312–375, flat bottom x 78–177 at y 375
 * Every path here runs along the centre of the ink line, so the ink's outer edge lands on those
 * edges (within a pixel). toon-art.html?section=props-align overlays the two to check.
 */
import { INK, INK_SOFT } from "../palette.js";
import { TAU, glint, inkStyle, seededRandom, sparklePath } from "../toon-kit.js";
import { FLASK_IMAGE_ASPECT_RATIO } from "../../../config/constants.js";
import { FLASK_CRACK_ORIGINS } from "../../../flask/flask-damage-shards.js";
import { polyPath, ribbonPath } from "./prop-kit.js";

var FRAME_W = 256;
var FRAME_H = 383;
var CX = 128;
/** Body ink in frame px (≈ 5 px on a desktop flask, ≈ 3 px on a phone). */
var LINE = 12;
var DETAIL = 5;

var LIP_Y = 22;
var LIP_RX = 48.5;
var LIP_RY = 9;
var LIP_BAND = 11;
var NECK_HW = 39.5;
var NECK_END = 110;
var KNEE_Y = 131;
var CONE_END = 300;
var BASE_Y = 370;
/** Liquid surface: a shallow pool of potion in the base. */
var LIQUID_Y = 318;

function coneHalf(y) {
  return 43.6 + 0.394 * (y - 132);
}

/** Glass and potion colours: pale enough to read as glass on the noon sky and at sunset. */
var GLASS = {
  body: "rgba(214, 245, 255, 0.5)",
  shade: "rgba(110, 196, 232, 0.42)",
  lip: "rgba(196, 238, 252, 0.92)",
  lipShade: "#93d3ec",
  rimTop: "#effcff",
  hole: "#6fc0de",
  holeDeep: "#4a9fc4",
  stripe: "rgba(255, 255, 255, 0.9)",
};
var POTION = {
  base: "#55d0e6",
  light: "#a8f0fa",
  shade: "#2fa6cf",
  edge: "#1f86b3",
};

/**
 * The flask silhouette, grown outward by `g` (negative shrinks it, for clips inside the ink).
 * @param {CanvasRenderingContext2D} c
 * @param {number} g
 */
export function flaskPath(c, g) {
  var rx = LIP_RX + g, ry = LIP_RY + Math.max(-LIP_RY + 1, g * 0.6);
  var neck = NECK_HW + g;
  var lipBot = LIP_Y + LIP_BAND;
  var th = Math.acos(Math.max(-1, Math.min(1, neck / rx)));
  var dropY = lipBot + ry * Math.sin(th);
  c.moveTo(CX - rx, LIP_Y);
  c.ellipse(CX, LIP_Y, rx, ry, 0, Math.PI, TAU);
  c.lineTo(CX + rx, lipBot);
  c.ellipse(CX, lipBot, rx, ry, 0, 0, th);
  c.lineTo(CX + neck, NECK_END);
  c.quadraticCurveTo(CX + neck, 121, CX + coneHalf(KNEE_Y) + g, KNEE_Y);
  c.lineTo(CX + coneHalf(CONE_END) + g, CONE_END);
  c.bezierCurveTo(CX + 125.6 + g, 340, CX + 97 + g, BASE_Y + g, CX + 44, BASE_Y + g);
  c.lineTo(CX - 44, BASE_Y + g);
  c.bezierCurveTo(CX - 97 - g, BASE_Y + g, CX - 125.6 - g, 340, CX - coneHalf(CONE_END) - g, CONE_END);
  c.lineTo(CX - coneHalf(KNEE_Y) - g, KNEE_Y);
  c.quadraticCurveTo(CX - neck, 121, CX - neck, NECK_END);
  c.lineTo(CX - neck, dropY);
  c.ellipse(CX, lipBot, rx, ry, 0, Math.PI - th, Math.PI);
  c.closePath();
}

/** The lip: a short glass cylinder seen from a little above. */
function lipPath(c) {
  c.moveTo(CX - LIP_RX, LIP_Y);
  c.ellipse(CX, LIP_Y, LIP_RX, LIP_RY, 0, Math.PI, TAU);
  c.lineTo(CX + LIP_RX, LIP_Y + LIP_BAND);
  c.ellipse(CX, LIP_Y + LIP_BAND, LIP_RX, LIP_RY, 0, 0, Math.PI);
  c.closePath();
}

/** A straight highlight stripe with round ends (a bold toy-glass sheen). */
function stripe(c, x0, y0, x1, y1, w, color) {
  c.beginPath();
  c.moveTo(x0, y0);
  c.lineTo(x1, y1);
  c.lineCap = "round";
  c.lineWidth = w;
  c.strokeStyle = color;
  c.stroke();
}

function bubble(c, x, y, r) {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.fillStyle = "rgba(255,255,255,0.35)";
  c.fill();
  c.lineWidth = Math.max(1.6, r * 0.32);
  c.strokeStyle = "rgba(255,255,255,0.95)";
  c.stroke();
  c.beginPath();
  c.arc(x - r * 0.35, y - r * 0.35, r * 0.28, 0, TAU);
  c.fillStyle = "#ffffff";
  c.fill();
}

/** flask.png 256×383 */
export function drawFlask(c) {
  // 1. Glass body: one translucent pale fill, so the sky shows through.
  c.beginPath();
  flaskPath(c, 0);
  c.fillStyle = GLASS.body;
  c.fill();

  c.save();
  c.beginPath();
  flaskPath(c, -LINE * 0.5);
  c.clip();

  // 2. Potion pool in the base, cel-shaded, with a lit surface ellipse.
  var lh = coneHalf(LIQUID_Y) + 12;
  c.save();
  c.beginPath();
  c.rect(0, LIQUID_Y, FRAME_W, FRAME_H - LIQUID_Y);
  c.clip();
  c.fillStyle = POTION.shade;
  c.fillRect(0, LIQUID_Y, FRAME_W, FRAME_H - LIQUID_Y);
  c.beginPath();
  c.save();
  c.translate(-15, -9);
  flaskPath(c, -LINE * 0.5);
  c.restore();
  c.fillStyle = POTION.base;
  c.fill();
  c.restore();
  c.beginPath();
  c.ellipse(CX, LIQUID_Y, lh, 8, 0, 0, TAU);
  c.fillStyle = POTION.light;
  c.fill();
  c.beginPath();
  c.ellipse(CX, LIQUID_Y, lh, 8, 0, 0.08, Math.PI - 0.08);
  inkStyle(c, 3, POTION.edge);
  c.stroke();

  // 3. Shade crescent down the right side and along the base: the body minus a copy of
  //    itself shifted up-left (the toon kit's cel recipe, done as a cut-out so the
  //    translucent glass is not filled twice).
  c.beginPath();
  c.rect(-10, -10, FRAME_W + 20, FRAME_H + 20);
  c.save();
  c.translate(-17, -8);
  flaskPath(c, 0);
  c.restore();
  c.fillStyle = GLASS.shade;
  c.fill("evenodd");

  // 4. Highlights: one bold stripe down the lit left side, a dash under it, a neck stripe
  //    and a thinner one on the right.
  var lx = function (y, inset) { return CX - coneHalf(y) + inset; };
  stripe(c, lx(156, 22), 156, lx(286, 24), 286, 15, GLASS.stripe);
  stripe(c, lx(306, 30), 306, lx(312, 31), 312, 11, GLASS.stripe);
  stripe(c, CX - NECK_HW + 15, 52, CX - NECK_HW + 15, 104, 9, GLASS.stripe);
  stripe(c, CX + coneHalf(168) - 20, 168, CX + coneHalf(214) - 21, 214, 6, "rgba(255,255,255,0.75)");
  stripe(c, CX + coneHalf(228) - 22, 228, CX + coneHalf(236) - 22, 236, 6, "rgba(255,255,255,0.75)");

  // 5. Graduation ticks: a lab-glass detail that also gives the body a front.
  inkStyle(c, 3.2, "rgba(31,92,128,0.55)");
  for (var k = 0; k < 3; k++) {
    var ty = 196 + k * 30;
    var tx = CX + coneHalf(ty) - 26;
    c.beginPath();
    c.moveTo(tx - (k === 1 ? 20 : 12), ty);
    c.lineTo(tx, ty);
    c.stroke();
  }

  // 6. Bubbles rising out of the potion.
  bubble(c, CX + 30, 344, 8.5);
  bubble(c, CX + 50, 330, 5);
  bubble(c, CX - 34, 352, 6);
  bubble(c, CX + 40, 300, 4);
  c.restore();

  // 7. The lip: glass cylinder, lit top ring, the dark mouth, shade on the right end.
  c.beginPath();
  lipPath(c);
  c.fillStyle = GLASS.lip;
  c.fill();
  c.save();
  c.beginPath();
  lipPath(c);
  c.clip();
  c.fillStyle = GLASS.lipShade;
  c.beginPath();
  c.ellipse(CX + 70, LIP_Y + LIP_BAND, 40, 30, 0, 0, TAU);
  c.fill();
  c.restore();
  c.beginPath();
  c.ellipse(CX, LIP_Y, LIP_RX, LIP_RY, 0, 0, TAU);
  c.fillStyle = GLASS.rimTop;
  c.fill();
  c.save();
  c.beginPath();
  c.ellipse(CX, LIP_Y + 0.5, 37, 5.6, 0, 0, TAU);
  c.fillStyle = GLASS.hole;
  c.fill();
  c.clip();
  c.beginPath();
  c.ellipse(CX + 6, LIP_Y - 3.5, 40, 6.5, 0, 0, TAU);
  c.fillStyle = GLASS.holeDeep;
  c.fill();
  c.restore();
  c.beginPath();
  c.ellipse(CX, LIP_Y + 0.5, 37, 5.6, 0, 0, TAU);
  inkStyle(c, DETAIL * 0.8, INK);
  c.stroke();
  // Front edge of the rim's top ring, then the lip's lower edge where it meets the neck.
  c.beginPath();
  c.ellipse(CX, LIP_Y, LIP_RX, LIP_RY, 0, 0.05, Math.PI - 0.05);
  inkStyle(c, DETAIL, INK);
  c.stroke();
  stripe(c, CX - 36, LIP_Y + 8, CX - 36, LIP_Y + 14, 6, "#ffffff");

  // 8. One bold ink silhouette over everything, then the lip's underside line on top.
  c.beginPath();
  flaskPath(c, 0);
  inkStyle(c, LINE, INK);
  c.stroke();
  c.beginPath();
  c.ellipse(CX, LIP_Y + LIP_BAND, LIP_RX, LIP_RY, 0, 0.06, Math.PI - 0.06);
  inkStyle(c, DETAIL, INK);
  c.stroke();

  glint(c, CX - 30, LIP_Y - 3, 5);
  glint(c, lx(140, 26), 140, 6.5);
}

// ---- cracks --------------------------------------------------------------------------------

/**
 * Impact points in this frame. FLASK_CRACK_ORIGINS are fractions of the flask *box* (the
 * collider rect, 1 : FLASK_IMAGE_ASPECT_RATIO); the skin fills the box height and is
 * letterboxed to 256/383 of the height across, centred.
 * @param {{ centerX: number, centerY: number }} o
 * @returns {number[]} [x, y]
 */
export function crackOriginInFrame(o) {
  var boxW = FRAME_H / FLASK_IMAGE_ASPECT_RATIO;
  var left = (boxW - FRAME_W) / 2;
  return [o.centerX * boxW - left, o.centerY * FRAME_H];
}

/**
 * A seeded impact crack: jagged rays of uneven length from a knocked-out chip, a branch off
 * some of them, and (for heavy damage) a web segment joining two neighbouring rays.
 */
function crackNetwork(ox, oy, o) {
  var rnd = seededRandom(o.seed);
  var strokes = [];
  var rays = [];
  var a0 = o.turn != null ? o.turn : rnd() * TAU;
  for (var i = 0; i < o.rays; i++) {
    var ang = a0 + (i / o.rays) * TAU + (rnd() - 0.5) * (TAU / o.rays) * 0.5;
    var len = o.len * (i % 2 ? 0.55 + rnd() * 0.3 : 0.9 + rnd() * 0.35);
    var segs = 3 + Math.floor(rnd() * 2);
    var heading = ang;
    var x = ox + Math.cos(ang) * o.chip * 0.5, y = oy + Math.sin(ang) * o.chip * 0.5;
    var pts = [[x, y]];
    var left = len - o.chip * 0.5;
    for (var s = 0; s < segs; s++) {
      // Uneven steps that swing hard left/right: a sharp zig-zag, never a smooth curve.
      var step = left * (s === segs - 1 ? 1 : 0.25 + rnd() * 0.25);
      left -= step;
      var swing = (s % 2 ? 1 : -1) * (0.35 + rnd() * 0.4);
      var dir = heading + swing;
      x += Math.cos(dir) * step;
      y += Math.sin(dir) * step;
      pts.push([x, y]);
      heading = ang + (rnd() - 0.5) * 0.25;
    }
    rays.push(pts);
    strokes.push({ pts: pts, w0: o.w, w1: 0.6 });
    if (rnd() < o.branch) {
      var from = pts[1 + Math.floor(rnd() * (pts.length - 2))];
      // Branch well off the ray so the two never run side by side like a fork.
      var bAng = ang + (rnd() < 0.5 ? -1 : 1) * (0.85 + rnd() * 0.45);
      var bLen = len * (0.25 + rnd() * 0.2);
      var mid = [from[0] + Math.cos(bAng - 0.3) * bLen * 0.55, from[1] + Math.sin(bAng - 0.3) * bLen * 0.55];
      strokes.push({
        pts: [from, mid, [mid[0] + Math.cos(bAng + 0.25) * bLen * 0.45, mid[1] + Math.sin(bAng + 0.25) * bLen * 0.45]],
        w0: o.w * 0.5,
        w1: 0.5,
      });
    }
  }
  // Web: a short kinked link between two neighbouring rays, the spider-web of a hard hit.
  for (var r = 0; r < o.web; r++) {
    var A = rays[(r * 2) % rays.length], B = rays[(r * 2 + 1) % rays.length];
    var pa = A[1], pb = B[1];
    var mx = (pa[0] + pb[0]) / 2, my = (pa[1] + pb[1]) / 2;
    var nx = mx - ox, ny = my - oy, nl = Math.hypot(nx, ny) || 1;
    strokes.push({
      pts: [pa, [mx + (nx / nl) * 7, my + (ny / nl) * 7], pb],
      w0: o.w * 0.45,
      w1: o.w * 0.3,
    });
  }
  // The chip: a jagged star-shaped hole, its points running into the rays.
  var chip = [];
  for (var k = 0; k < 10; k++) {
    var ca = a0 + (k / 10) * TAU + (rnd() - 0.5) * 0.25;
    var cr = o.chip * (k % 2 ? 0.5 + rnd() * 0.15 : 0.95 + rnd() * 0.3);
    chip.push([ox + Math.cos(ca) * cr, oy + Math.sin(ca) * cr]);
  }
  return { strokes: strokes, chip: chip, ox: ox, oy: oy };
}

function paintCracks(c, net, o) {
  var i;
  c.save();
  c.beginPath();
  flaskPath(c, -LINE * 0.35);
  c.clip();
  c.lineJoin = "round";
  // White edge first (a wider copy), so every ink crack carries a bright fracture edge.
  c.fillStyle = c.strokeStyle = "rgba(255,255,255,0.95)";
  c.lineWidth = o.edge;
  for (i = 0; i < net.strokes.length; i++) {
    c.beginPath();
    ribbonPath(c, net.strokes[i].pts, net.strokes[i].w0, net.strokes[i].w1);
    c.fill();
    c.stroke();
  }
  c.beginPath();
  polyPath(c, net.chip, 1, 1, 0, 0);
  c.stroke();
  c.fillStyle = INK;
  for (i = 0; i < net.strokes.length; i++) {
    c.beginPath();
    ribbonPath(c, net.strokes[i].pts, net.strokes[i].w0, net.strokes[i].w1);
    c.fill();
  }
  // The chip knocked out at the impact: bright glass with a shaded inner facet.
  c.beginPath();
  polyPath(c, net.chip, 1, 1, 0, 0);
  c.fillStyle = "#f2fcff";
  c.fill();
  c.save();
  c.clip();
  c.beginPath();
  polyPath(c, net.chip, 1, 1, o.chip * 0.3, o.chip * 0.35);
  c.fillStyle = "#a9def3";
  c.fill();
  c.restore();
  c.beginPath();
  polyPath(c, net.chip, 1, 1, 0, 0);
  inkStyle(c, o.w * 0.6, INK);
  c.stroke();
  c.beginPath();
  sparklePath(c, net.ox - o.chip * 0.2, net.oy - o.chip * 0.25, o.chip * 0.5, 0.2);
  c.fillStyle = "#ffffff";
  c.fill();
  c.restore();
}

/**
 * Crack recipes. Widths are frame px: the flask is drawn at ≈ 0.25–0.5× on screen, so a
 * ray needs ~10 px here to stay a bold line on a phone.
 */
var CRACK_1 = { seed: 11, rays: 5, len: 76, w: 10, chip: 15, web: 0, branch: 0.5, edge: 6.5, turn: -0.4 };
var CRACK_2 = { seed: 47, rays: 6, len: 90, w: 10.5, chip: 17, web: 1, branch: 0.6, edge: 6.5, turn: 0.3 };

/** crack1.png 256×383, overlay aligned with the flask frame */
export function drawFlaskCrack1(c) {
  var p = crackOriginInFrame(FLASK_CRACK_ORIGINS[0]);
  paintCracks(c, crackNetwork(p[0], p[1], CRACK_1), CRACK_1);
}

/** crack2.png 256×383, overlay aligned with the flask frame (stacked over crack1) */
export function drawFlaskCrack2(c) {
  var p = crackOriginInFrame(FLASK_CRACK_ORIGINS[1]);
  paintCracks(c, crackNetwork(p[0], p[1], CRACK_2), CRACK_2);
}

// ---- shards --------------------------------------------------------------------------------

/** Glass for flying shards: more solid than the flask so a small piece still reads in the air. */
var SHARD_TONE = { base: "#b4e9ff", light: "#ffffff", shade: "#5cb4dc", facet: "#8ad4f2" };

/**
 * A cel-faceted glass shard centred in a w×h frame. `pts` are unit points (±1); `facet`
 * is the index pair [vertex, vertex] whose chord splits off the darker back facet.
 */
function paintShard(c, w, h, pts, facet, lit) {
  // Fill the frame: shards fly at only ~20–40 px, so every pixel of silhouette counts.
  var R = Math.min(w, h) * 0.45;
  var ox = w / 2, oy = h / 2;
  var line = R * 0.15;
  c.save();
  c.lineJoin = "round";
  c.beginPath();
  polyPath(c, pts, R, R, ox, oy);
  c.fillStyle = SHARD_TONE.shade;
  c.fill();
  c.save();
  c.clip();
  // Lit body shifted up-left leaves a shade crescent on the lower right.
  c.beginPath();
  polyPath(c, pts, R * 0.97, R * 0.97, ox - R * 0.1, oy - R * 0.13);
  c.fillStyle = SHARD_TONE.base;
  c.fill();
  // A second facet across the piece, a tone deeper.
  var a = pts[facet[0]], b = pts[facet[1]];
  c.beginPath();
  c.moveTo(ox + a[0] * R, oy + a[1] * R);
  c.lineTo(ox + b[0] * R, oy + b[1] * R);
  c.lineTo(ox + R * 2, oy + R * 2);
  c.closePath();
  c.fillStyle = SHARD_TONE.facet;
  c.fill();
  // Bright lit facet on the upper-left edge.
  c.beginPath();
  polyPath(c, lit, R, R, ox, oy);
  c.fillStyle = "rgba(255,255,255,0.85)";
  c.fill();
  c.restore();
  c.beginPath();
  c.moveTo(ox + a[0] * R, oy + a[1] * R);
  c.lineTo(ox + b[0] * R, oy + b[1] * R);
  inkStyle(c, line * 0.45, INK_SOFT);
  c.stroke();
  c.beginPath();
  polyPath(c, pts, R, R, ox, oy);
  inkStyle(c, line, INK);
  c.stroke();
  c.restore();
}

var SHARD_1 = [[-0.62, -0.92], [0.18, -0.18], [0.92, 0.66], [0.12, 0.6], [-0.86, 0.74], [-0.72, -0.08]];
var SHARD_1_LIT = [[-0.56, -0.66], [-0.4, -0.38], [-0.62, 0.36], [-0.7, -0.04]];
var SHARD_2 = [[-0.95, -0.28], [0.72, -0.9], [0.5, 0.06], [0.92, 0.78], [0.3, 0.42], [-0.18, 0.62]];
var SHARD_2_LIT = [[-0.7, -0.26], [0.4, -0.66], [0.3, -0.5], [-0.52, -0.1]];

/** shard-1.png 128×128 */
export function drawShard1(c, w, h) {
  paintShard(c, w, h, SHARD_1, [1, 4], SHARD_1_LIT);
  glint(c, w * 0.32, h * 0.36, Math.min(w, h) * 0.035);
}

/** shard-2.png 128×128 */
export function drawShard2(c, w, h) {
  paintShard(c, w, h, SHARD_2, [2, 5], SHARD_2_LIT);
  glint(c, w * 0.3, h * 0.42, Math.min(w, h) * 0.035);
}
